import * as React from 'react';
import { useEffect } from 'react';
import dateUtil from '../../../utils/dateUtil';
import httpService from '../../../core/services/HttpService';
import TimeTrackingService from '../services/TimeTrackingService';
import {
  WorkArea,
  TimeTracking,
  NormalizedImport,
  PreviewSummary,
  normalizeRows,
  sameMinute,
  getExistingBreakMin,
  getWA,
  isUnchanged,
} from './ttImportUtils';
import css from './TTImportWizard.module.css';

const STATUS_COLOR: Record<string, string> = {
  error: '#ef4444',
  warn: '#f59e0b',
  skip: '#94a3b8',
};
const STATUS_COLOR_DEFAULT = '#22c55e';

interface TTImportPreviewStepProps {
  open: boolean;
  importStep: number;
  importRows: any[];
  importMapping: Record<string, string>;
  importSamples: number;
  workAreas: WorkArea[];
  previewLoading: boolean;
  previewError: string;
  previewRows: any[];
  previewSummary: PreviewSummary;
  setPreviewLoading: (v: boolean) => void;
  setPreviewError: (v: string) => void;
  setPreviewRows: (v: any[]) => void;
  setPreviewSummary: (v: PreviewSummary) => void;
  setExistingTTs: (v: TimeTracking[]) => void;
  setImportStep: (v: number) => void;
}

export function TTImportPreviewStep({
  open,
  importStep,
  importRows,
  importMapping,
  importSamples,
  workAreas,
  previewLoading,
  previewError,
  previewRows,
  previewSummary,
  setPreviewLoading,
  setPreviewError,
  setPreviewRows,
  setPreviewSummary,
  setExistingTTs,
  setImportStep,
}: TTImportPreviewStepProps): React.JSX.Element | null {

  const runDryRun = async (normalized: NormalizedImport[], existing: TimeTracking[]) => {
    const idToExisting = new Map<number, TimeTracking>();
    const keyToExisting = new Map<string, TimeTracking[]>();
    const dayKeyToExisting = new Map<string, TimeTracking[]>();
    const dayAnyWAKeyToExisting = new Map<string, TimeTracking[]>();

    existing.forEach((tt) => {
      idToExisting.set(tt.id, tt);
      const emp = tt.employee;
      const wa = getWA(tt);
      const st = dateUtil.convertUTCToTenantTime(tt.start_time);
      const en = dateUtil.convertUTCToTenantTime(tt.end_time);
      if (!st || !en) return;
      const dayStr = `${st.getFullYear()}-${String(st.getMonth() + 1).padStart(2, '0')}-${String(st.getDate()).padStart(2, '0')}`;
      const hh = String(st.getHours()).padStart(2, '0');
      const mm = String(st.getMinutes()).padStart(2, '0');
      const eh = String(en.getHours()).padStart(2, '0');
      const em = String(en.getMinutes()).padStart(2, '0');
      const timeKey = `${emp}|${wa}|${dayStr}|${hh}:${mm}|${eh}:${em}`;
      const dayKey = `${emp}|${wa}|${dayStr}`;
      const anyKey = `${emp}|${dayStr}`;
      const push = (map: Map<string, TimeTracking[]>, k: string) => {
        const arr = map.get(k);
        if (arr) arr.push(tt);
        else map.set(k, [tt]);
      };
      push(keyToExisting, timeKey);
      push(dayKeyToExisting, dayKey);
      push(dayAnyWAKeyToExisting, anyKey);
    });

    const crCandidates = normalized.filter((n) => n.type === 'CR' && !n.error);
    const payloadRows = crCandidates
      .filter((n) => {
        const ex = n.ttId ? idToExisting.get(n.ttId) : undefined;
        if (!ex) return true;
        return !isUnchanged(n, ex);
      })
      .map((n) => ({
        time_tracking: n.ttId || undefined,
        employee: n.employeeId,
        new_start_time: n.startIsoUtc,
        new_end_time: n.endIsoUtc,
        break_minutes: n.breakMinutes ?? undefined,
        new_work_area: n.workAreaId ?? undefined,
      }));

    let crResults: any[] = [];
    try {
      if (payloadRows.length > 0) {
        try {
          const res = await httpService.post('/api/time-trackings/imports:dry-run', {
            rows: payloadRows,
          });
          crResults = (res.data?.results || []) as any[];
        } catch {
          const res2 = await httpService.post(
            '/api/time-tracking/timetrackings/imports:dry-run',
            { rows: payloadRows }
          );
          crResults = (res2.data?.results || []) as any[];
        }
      }
    } catch {
      crResults = payloadRows.map((_, idx) => ({
        row: idx,
        status: 'warn',
        detail: 'Dry-run not available',
      }));
    }

    const crPayloadIndex = new Map<NormalizedImport, number>();
    let p = 0;
    crCandidates.forEach((n) => {
      const ex = n.ttId ? idToExisting.get(n.ttId) : undefined;
      if (!ex || !isUnchanged(n, ex)) {
        crPayloadIndex.set(n, p++);
      }
    });

    const resultRows: any[] = [];
    normalized.forEach((n) => {
      if (n.error) {
        resultRows.push({ ...n, status: 'error', detail: n.error });
        return;
      }
      if (n.type === 'CR') {
        if (n.ttId) {
          const ex = idToExisting.get(n.ttId);
          if (ex && isUnchanged(n, ex)) {
            resultRows.push({ ...n, status: 'skip', detail: 'Unchanged \u2192 skip' });
            return;
          }
        }
        const idx = crPayloadIndex.get(n);
        if (idx != null) {
          const r = crResults[idx];
          const status = r?.status || 'ok';
          const detail = r?.detail || '';
          resultRows.push({ ...n, status, detail });
        } else {
          resultRows.push({ ...n, status: 'ok', detail: 'Create CR' });
        }
        return;
      }

      const exact = keyToExisting.get(n.key || '') || [];
      if (exact.length === 1) {
        const hit = exact[0];
        if (isUnchanged(n, hit)) {
          resultRows.push({
            ...n,
            status: 'skip',
            detail: 'Unchanged (exact) \u2192 skip',
          });
        } else {
          resultRows.push({
            ...n,
            type: 'CR',
            ttId: hit.id,
            status: 'ok',
            detail: 'Exact match \u2192 CR',
          });
        }
        return;
      }
      if (exact.length > 1) {
        resultRows.push({ ...n, status: 'error', detail: 'Ambiguous match (exact)' });
        return;
      }

      const dayKey =
        n.workAreaId != null ? `${n.employeeId}|${n.workAreaId}|${n.day}` : null;
      const dayList = dayKey ? dayKeyToExisting.get(dayKey) || [] : [];
      if (dayList.length === 1) {
        const hit = dayList[0];
        if (isUnchanged(n, hit)) {
          resultRows.push({
            ...n,
            status: 'skip',
            detail: 'Unchanged (same day & WA) \u2192 skip',
          });
        } else {
          resultRows.push({
            ...n,
            type: 'CR',
            ttId: hit.id,
            status: 'ok',
            detail: 'Unique day match (same WA) \u2192 CR',
          });
        }
        return;
      }
      if (dayList.length > 1) {
        resultRows.push({
          ...n,
          status: 'error',
          detail: 'Ambiguous match (same day & WA)',
        });
        return;
      }

      const anyList =
        dayAnyWAKeyToExisting.get(`${n.employeeId}|${n.day}`) || [];
      if (anyList.length === 1) {
        const hit = anyList[0];
        const timesSame =
          sameMinute(n.startIsoUtc, hit.start_time) &&
          sameMinute(n.endIsoUtc, hit.end_time);
        let breakSame = true;
        if (n.breakMinutes != null) {
          breakSame =
            Number(n.breakMinutes) === Number(getExistingBreakMin(hit));
        }
        if (timesSame && breakSame) {
          resultRows.push({
            ...n,
            status: 'skip',
            detail: 'Unchanged (same day) \u2192 skip',
          });
          return;
        }
        const waChanged = n.workAreaId != null && n.workAreaId !== getWA(hit);
        resultRows.push({
          ...n,
          type: 'CR',
          ttId: hit.id,
          status: 'ok',
          detail: waChanged
            ? 'Unique day \u2192 CR (WA change)'
            : 'Unique day \u2192 CR',
        });
        return;
      }
      if (anyList.length > 1) {
        resultRows.push({
          ...n,
          status: 'error',
          detail: 'Ambiguous match (same day, multiple TTs)',
        });
        return;
      }

      resultRows.push({ ...n, status: 'ok', detail: 'Create new' });
    });

    const sum = {
      okNew: 0,
      okCr: 0,
      warn: 0,
      error: 0,
      skipped: importSamples || 0,
    };
    resultRows.forEach((r) => {
      if (r.status === 'ok') {
        if (r.type === 'NEW') sum.okNew++;
        else sum.okCr++;
      } else if (r.status === 'warn') {
        sum.warn++;
      } else if (r.status === 'error') {
        sum.error++;
      } else if (r.status === 'skip') {
        sum.skipped++;
      }
    });

    setPreviewRows(resultRows);
    setPreviewSummary(sum);
  };

  useEffect(() => {
    const loadPreview = async () => {
      if (!open || importStep !== 3) return;
      if (!importRows || importRows.length === 0) {
        setPreviewRows([]);
        setPreviewSummary({
          okNew: 0,
          okCr: 0,
          warn: 0,
          error: 0,
          skipped: importSamples || 0,
        });
        return;
      }
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const normalized = normalizeRows(importRows, importMapping, workAreas);
        const days = normalized
          .map((n) => n.day)
          .filter(Boolean)
          .sort();
        let existing: TimeTracking[] = [];
        if (days.length > 0) {
          const start = days[0];
          const end = days[days.length - 1];
          const employeeIds = Array.from(
            new Set(normalized.map((n) => n.employeeId))
          ).filter(Boolean) as number[];
          try {
            existing =
              await TimeTrackingService.fetchTimeTrackingsWithChangeRequests({
                start_date: start,
                end_date: end,
                employee: employeeIds.length
                  ? employeeIds.join(',')
                  : undefined,
              } as any);
            setExistingTTs(existing);
          } catch {
            try {
              existing = await TimeTrackingService.fetchTimeTrackings({
                start_date: start,
                end_date: end,
                employee: employeeIds.length
                  ? employeeIds.join(',')
                  : undefined,
              } as any);
              setExistingTTs(existing);
            } catch {
              try {
                existing = await TimeTrackingService.fetchTimeTrackings({
                  start: start,
                  end: end,
                  employee: employeeIds.length
                    ? employeeIds.join(',')
                    : undefined,
                } as any);
                setExistingTTs(existing);
              } catch {
                const seen = new Map<number, TimeTracking>();
                for (const id of employeeIds) {
                  try {
                    const part =
                      await TimeTrackingService.fetchTimeTrackings({
                        start_date: start,
                        end_date: end,
                        employee: id,
                      } as any);
                    (part || []).forEach((tt: TimeTracking) =>
                      seen.set(tt.id, tt)
                    );
                  } catch {}
                }
                existing = Array.from(seen.values());
                setExistingTTs(existing);
              }
            }
          }
        }
        await runDryRun(normalized, existing);
      } catch (e: any) {
        setPreviewError(e?.message || 'Preview failed');
      } finally {
        setPreviewLoading(false);
      }
    };
    loadPreview();
  }, [open, importStep, importRows, importMapping, importSamples, workAreas]);

  if (importStep !== 3) return null;

  return (
    <div className={css.previewBlock}>
      <div className={css.previewTitle}>Preview (Dry-Run)</div>
      {previewLoading && (
        <div className={css.previewLoading}>Loading preview&hellip;</div>
      )}
      {previewError && (
        <div className={css.previewError}>{previewError}</div>
      )}
      {!previewLoading && !previewError && (
        <>
          <div className={css.summaryLine}>
            <strong className={css.summaryStrong}>Summary:</strong> OK-New{' '}
            {previewSummary.okNew} &middot; OK-CR {previewSummary.okCr}{' '}
            &middot; Warnings {previewSummary.warn} &middot; Errors{' '}
            {previewSummary.error} &middot; Skipped {previewSummary.skipped}
          </div>
          <div className={css.tableWrap}>
            <table className={css.previewTable}>
              <thead>
                <tr className={css.previewThead}>
                  <th className={css.previewTh}>Row</th>
                  <th className={css.previewTh}>Status</th>
                  <th className={css.previewTh}>Employee</th>
                  <th className={css.previewTh}>Work Area</th>
                  <th className={css.previewTh}>Day</th>
                  <th className={css.previewTh}>Start&ndash;End</th>
                  <th className={css.previewTh}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 200).map((r, i) => {
                  const color =
                    STATUS_COLOR[r.status] || STATUS_COLOR_DEFAULT;
                  return (
                    <tr
                      key={i}
                      style={{
                        background:
                          i % 2 === 0 ? 'transparent' : '#f9fafb',
                      }}
                    >
                      <td className={css.previewTdMuted}>
                        {r.rowIndex}
                      </td>
                      <td
                        className={css.previewTd}
                        style={{ color, fontWeight: 600 }}
                      >
                        {r.status}
                      </td>
                      <td className={css.previewTd}>
                        {r.employeeId}{' '}
                        {r.employeeName ? `(${r.employeeName})` : ''}
                      </td>
                      <td className={css.previewTd}>
                        {r.workAreaId ?? ''}{' '}
                        {r.workAreaName ? `(${r.workAreaName})` : ''}
                      </td>
                      <td className={css.previewTd}>{r.day}</td>
                      <td className={css.previewTd}>
                        {r.startHm} &ndash; {r.endHm}
                      </td>
                      <td className={css.previewTdMuted}>
                        {r.detail || ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className={css.previewActions}>
        <button
          className={css.btnBack}
          onClick={() => setImportStep(2)}
        >
          Back
        </button>
        <button
          className={css.btnPrimary}
          disabled={previewLoading}
          onClick={() => setImportStep(4)}
          data-testid="tt-import-next-button"
        >
          Next
        </button>
      </div>
    </div>
  );
}
